import { useState, useEffect } from 'react';
import { db } from '@/config/firebase';
import { 
  collection, 
  query, 
  onSnapshot, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  where,
  orderBy,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

// Hook for real-time collection data
export const useCollection = (collectionName, constraints = []) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let q = collection(db, collectionName);
    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDocuments(docs);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [collectionName, JSON.stringify(constraints)]);

  return { documents, loading, error };
};

// Hook for real-time document data
export const useDocument = (collectionName, docId) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!docId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(doc(db, collectionName, docId),
      (doc) => {
        if (doc.exists()) {
          setDocument({ id: doc.id, ...doc.data() });
        } else {
          setDocument(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [collectionName, docId]);

  return { document, loading, error };
};

// CRUD operations
export const addDocument = async (collectionName, data) => {
  const id = uuidv4();
  const docRef = doc(db, collectionName, id);
  await updateDoc(docRef, { ...data, id, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }).catch(async () => {
    const { setDoc } = await import('firebase/firestore');
    await setDoc(docRef, { ...data, id, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
  return id;
};

export const updateDocument = async (collectionName, docId, data) => {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
};

export const deleteDocument = async (collectionName, docId) => {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
};

export { where, orderBy, serverTimestamp };
